/* eslint-disable react/no-this-in-sfc */
import React, { useState, useContext } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig } from '@galaxy/lorule-react-commons';
import moment from 'moment-timezone';
import { Delegate } from '../../../api/delegateApiTypes';
import ConfigContext from '../../../contexts/ConfigContext';
import { sendToast } from '../../../utils/displayUtils';
import NinModal from '../../common/modal/NinModal';
import { INITIAL_SELECT_ID } from '../../../utils/Constants';
import { createDelegate, updateDelegate } from '../../../api/delegateApi';
import DelegateForm, { DelegateModalForm } from './DelegateForm';
import { FormType } from '../../common/formTypes';

export interface DelegateModalFormBaseProps {
    title: String;
    formType: FormType;
    toggleButton: JSX.Element;
    onSubmit?: () => void;
}

export interface DelegateModalProps extends DelegateModalFormBaseProps {
    id?: number;
    personId?: number;
    proxyId?: number;
    proxyName?: string;
    description?: string | null;
    startDate?: string;
    endDate?: string | null;
    active?: boolean;
}

const submitDelegate = async (
    config: LoruleRequestConfig,
    formType: FormType,
    values: DelegateModalForm,
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        const delegate: Delegate = {
            id: values.id,
            personId: values.personId || -1,
            proxyName: values.proxyName,
            proxyId: values.proxyId || -1,
            description: values.description,
            startDate: values.startDate,
            endDate: values.endDate,
            active: values.active,
        };
        if (formType === FormType.CREATE) {
            await createDelegate(config, delegate);
            sendToast(
                <>
                    Created Delegate: <strong>{values.proxyName}</strong>
                </>,
            );
        } else {
            await updateDelegate(config, delegate);
            sendToast(
                <>
                    Updated Delegate: <strong>{values.proxyName}</strong>
                </>,
            );
        }

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`${formType.toLowerCase()} failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const DelegateModal = (props: DelegateModalProps) => {
    const config = useContext(ConfigContext);
    const [responseError, setResponseError] = useState('');

    const createDelegateValidationSchema = Yup.object().shape({
        proxyId: Yup.number()
            .required('Delegate is required')
            .test(
                'id validation',
                'Delegate cannot be the same as Person',
                (proxyId) => proxyId !== props.personId,
            ),
        description: Yup.string()
            .nullable()
            // eslint-disable-next-line no-template-curly-in-string
            .max(200, 'Description exceeds max length of ${max} characters')
            .matches(
                /^[\x20-\x7F]*$/,
                'Description must consist of only ASCII characters (check for MS Word hyphenation)',
            ),
        startDate: Yup.date().required('Start Date is required'),
        endDate: Yup.date()
            .nullable()
            .test('after-check', 'End Date must not be before Start Date', function test(endDate) {
                if (!endDate) {
                    return true;
                }
                const startMoment = moment(this.parent.startDate);
                const endMoment = moment(endDate);
                return !endMoment.isBefore(startMoment);
            }),
    });

    const initialValues: DelegateModalForm = {
        id: props.id,
        personId: props.personId ? props.personId : INITIAL_SELECT_ID,
        proxyId: props.proxyId ? props.proxyId : INITIAL_SELECT_ID,
        proxyName: props.proxyName ? props.proxyName : '',
        description: props.description ? props.description : '',
        startDate: props.startDate ? props.startDate : new Date().toISOString(),
        endDate: props.endDate ? props.endDate : '',
        active: props.active ? props.active : true,
    };

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={createDelegateValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitDelegate(
                            config,
                            props.formType,
                            values,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={(formikProps) => (
                        <DelegateForm
                            {...formikProps}
                            responseError={responseError}
                            formType={props.formType}
                            setResponseError={setResponseError}
                            toggleModal={toggleModal}
                        />
                    )}
                />
            )}
        />
    );
};

export default DelegateModal;
